import { BrowserDataCacheQuery } from '../core/types';
import { BrowserDataCacheSqlValue } from './driver';

export interface BrowserDataCacheTableMapping<TRow = unknown> {
  table: string;
  primaryKey: string[];
  columns: string[];
  toColumnValues(row: TRow): Record<string, BrowserDataCacheSqlValue>;
  queryWhere?(query: BrowserDataCacheQuery): Record<string, BrowserDataCacheSqlValue>;
}

export interface BrowserDataCacheSqlStatement {
  sql: string;
  params: BrowserDataCacheSqlValue[];
}

export function buildUpsertStatement<TRow>(
  mapping: BrowserDataCacheTableMapping<TRow>,
  row: TRow,
): BrowserDataCacheSqlStatement {
  const values = mapping.toColumnValues(row);
  const columns = mapping.columns.filter((column) => Object.prototype.hasOwnProperty.call(values, column));
  if (!columns.length) {
    throw new Error(`No mapped columns for table ${mapping.table}`);
  }
  for (const key of mapping.primaryKey) {
    if (!columns.includes(key)) {
      throw new Error(`Missing primary key column ${key} for table ${mapping.table}`);
    }
  }

  const updateColumns = columns.filter((column) => !mapping.primaryKey.includes(column));
  const conflict = mapping.primaryKey.map(quoteIdentifier).join(', ');
  const updateSql = updateColumns.length
    ? ` DO UPDATE SET ${updateColumns.map((column) => `${quoteIdentifier(column)}=excluded.${quoteIdentifier(column)}`).join(', ')}`
    : ' DO NOTHING';
  return {
    sql: `INSERT INTO ${quoteIdentifier(mapping.table)} (${columns.map(quoteIdentifier).join(', ')}) VALUES (${columns.map(() => '?').join(', ')}) ON CONFLICT(${conflict})${updateSql}`,
    params: columns.map((column) => values[column] ?? null),
  };
}

export function buildSelectStatement<TRow>(
  mapping: BrowserDataCacheTableMapping<TRow>,
  query: BrowserDataCacheQuery = {},
): BrowserDataCacheSqlStatement {
  const where = mapping.queryWhere ? mapping.queryWhere(query) : filterWhereByColumns(query.where || {}, mapping.columns);
  const entries = Object.entries(where);
  const whereSql = entries.length
    ? ` WHERE ${entries.map(([column]) => `${quoteIdentifier(column)} = ?`).join(' AND ')}`
    : '';
  const limit = query.limit == null ? '' : ` LIMIT ${Math.max(0, Math.floor(Number(query.limit)) || 0)}`;
  const offset = query.offset == null ? '' : ` OFFSET ${Math.max(0, Math.floor(Number(query.offset)) || 0)}`;
  return {
    sql: `SELECT * FROM ${quoteIdentifier(mapping.table)}${whereSql}${limit}${offset}`,
    params: entries.map(([, value]) => value ?? null),
  };
}

export function quoteIdentifier(identifier: string): string {
  return `"${String(identifier || '').replace(/"/g, '""')}"`;
}

function filterWhereByColumns(where: Record<string, unknown>, columns: string[]): Record<string, BrowserDataCacheSqlValue> {
  const allowed = new Set(columns);
  const result: Record<string, BrowserDataCacheSqlValue> = {};
  for (const [key, value] of Object.entries(where || {})) {
    if (!allowed.has(key)) continue;
    if (isSqlValue(value)) {
      result[key] = value;
    }
  }
  return result;
}

function isSqlValue(value: unknown): value is BrowserDataCacheSqlValue {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value == null;
}
